import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { useToast } from '../context/ToastContext';
import { useConfirm } from '../context/ConfirmContext';
import AddProductsToCampaignModal from './AddProductsToCampaignModal';
import BrandstoreStockTab from './BrandstoreStockTab';
import BrandstoreVouchersTab, { CouponChip } from './BrandstoreVouchersTab';
import { PartnerReturnsTab, PartnerApiKeysTab } from './BrandstorePartnerTabs';
import { useAuth } from '../context/AuthContext';
import { BrandstoreAccessContext, brandstoreAccessFor, useBrandstoreAccess } from './brandstoreAccess';
import { fmtIstDate, fmtIstDateTime } from '../utils/dateUtils';
import { ORDER_STATUS_COLORS as STATUS_COLORS } from './brandstoreAdminShared';

const sanitizeForFilename = (s) => (s || '').toString().trim().replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '');

/** Sets document.title to a descriptive name just for the print/save-as-PDF dialog
 *  (that's where Chrome et al. pull the suggested filename from), then restores it. */
function printWithFilename(filename) {
  const prevTitle = document.title;
  document.title = filename;
  const restore = () => { document.title = prevTitle; window.removeEventListener('afterprint', restore); };
  window.addEventListener('afterprint', restore);
  window.print();
}

// ── Status helpers ────────────────────────────────────────────
const STATUS_ORDER  = ['ORDER_RECEIVED', 'CONFIRMED', 'PROCESSING', 'IN_TRANSIT', 'DELIVERED', 'RTO'];
const STATUS_NEXT   = { ORDER_RECEIVED: 'CONFIRMED', CONFIRMED: 'PROCESSING', PROCESSING: 'IN_TRANSIT', IN_TRANSIT: 'DELIVERED' };
const pill = (status) => {
  const c = STATUS_COLORS[status] || { bg: '#f5f5f5', color: '#555' };
  return { padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: c.bg, color: c.color, whiteSpace: 'nowrap', display: 'inline-block' };
};

// ── Shared styles ─────────────────────────────────────────────
const card  = { background: '#fff', border: '1.5px solid #E5E7EB', borderRadius: 12, padding: '20px 24px', marginBottom: 16 };
const label = { display: 'block', fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 5 };
const input = { width: '100%', padding: '9px 11px', border: '1.5px solid #E5E7EB', borderRadius: 7, fontSize: 14, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box', color: '#111827' };
const btnPrimary = { padding: '9px 20px', background: 'var(--color-primary,#F29213)', color: '#fff', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 700, cursor: 'pointer' };
const btnGhost  = { padding: '8px 16px', background: '#fff', color: '#4B5563', border: '1.5px solid #E5E7EB', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' };

// ── Company form modal ────────────────────────────────────────
// Storefront banner images (several rotate as a slideshow on the catalog). Changes
// save immediately rather than with the modal's Save button, since each upload has
// already reached S3 by the time it shows up here.
const MAX_BANNER_BYTES = 10 * 1024 * 1024;

function BannerManager({ tenantId }) {
  const [banners, setBanners] = useState([]);
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    api.get(`/admin/brandstore/tenants/${tenantId}/banners`)
      .then(r => setBanners(r.data || []))
      .catch(() => setError('Could not load banners.'));
  }, [tenantId]);

  const run = async (fn, failMsg) => {
    setBusy(true); setError('');
    try { await fn(); }
    catch (err) {
      setError(err.response?.status === 413
        ? 'That image is over the 10 MB upload limit. Export it as JPEG or WebP at 1500 × 500 px and try again.'
        : err.response?.data?.error || failMsg);
    }
    finally { setBusy(false); }
  };

  const upload = (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = '';
    if (!files.length) return;
    // Server rejects anything over spring.servlet.multipart.max-file-size with a bare 413.
    const tooBig = files.filter(f => f.size > MAX_BANNER_BYTES);
    if (tooBig.length) {
      setError(`${tooBig.map(f => `${f.name} (${(f.size / 1024 / 1024).toFixed(1)} MB)`).join(', ')} is over the 10 MB limit. Export it as JPEG or WebP at 1500 × 500 px; that's usually under 500 KB.`);
      return;
    }
    run(async () => {
      // Sequential so they land in the order picked and a failure stops the rest.
      for (const file of files) {
        const fd = new FormData();
        fd.append('banner', file);
        const res = await api.post(`/admin/brandstore/tenants/${tenantId}/banners`, fd);
        setBanners(b => [...b, res.data]);
      }
    }, 'Upload failed.');
  };

  const remove = (id) => run(async () => {
    await api.delete(`/admin/brandstore/tenants/${tenantId}/banners/${id}`);
    setBanners(b => b.filter(x => x.id !== id));
  }, 'Failed to remove banner.');

  const move = (index, delta) => run(async () => {
    const next = [...banners];
    [next[index], next[index + delta]] = [next[index + delta], next[index]];
    const res = await api.put(`/admin/brandstore/tenants/${tenantId}/banners/order`, next.map(b => b.id));
    setBanners(res.data || next);
  }, 'Failed to reorder banners.');

  const small = { ...btnGhost, padding: '4px 9px', fontSize: 12 };

  return (
    <div>
      {error && <div style={{ color: '#B91C1C', fontSize: 12, marginBottom: 8 }}>{error}</div>}
      {banners.map((b, i) => (
        <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <img src={b.url} alt={`Banner ${i + 1}`} style={{ width: 150, aspectRatio: '3 / 1', objectFit: 'cover', borderRadius: 6, border: '1.5px solid #E5E7EB', background: '#F9FAFB' }} />
          <span style={{ fontSize: 12, color: '#6B7280', minWidth: 18 }}>{i + 1}</span>
          <button type="button" style={small} disabled={busy || i === 0} onClick={() => move(i, -1)} aria-label={`Move banner ${i + 1} up`}>↑</button>
          <button type="button" style={small} disabled={busy || i === banners.length - 1} onClick={() => move(i, 1)} aria-label={`Move banner ${i + 1} down`}>↓</button>
          <button type="button" style={small} disabled={busy} onClick={() => remove(b.id)}>Remove</button>
        </div>
      ))}
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple onChange={upload} style={{ display: 'none' }} />
      <button type="button" style={{ ...btnGhost, padding: '6px 12px', fontSize: 12 }} disabled={busy || banners.length >= 10} onClick={() => fileRef.current?.click()}>
        {busy ? 'Saving…' : '+ Add banner'}
      </button>
    </div>
  );
}

function TenantModal({ tenant, onClose, onSaved }) {
  const [form, setForm] = useState({
    slug:            tenant?.slug            || '',
    displayName:     tenant?.displayName     || '',
    emailDomain:     tenant?.emailDomain     || '',
    primaryColor:    tenant?.primaryColor    || '#F29213',
    logoText:        tenant?.logoText        || '',
    fontFamily:      tenant?.fontFamily      || 'Inter',
    officeLocations: tenant?.officeLocations?.join('\n') || '',
    allowedEmails:   tenant?.allowedEmails?.join('\n')   || '',
    adminEmails:     tenant?.adminEmails?.join('\n')     || '',
    accessMode:      tenant?.accessMode       || 'LOGIN_REQUIRED',
    storefrontType:  tenant?.storefrontType   || 'CORPORATE',
    paymentMode:     tenant?.paymentMode      || 'COMPANY_PAID',
    pointsPriceDisplay: tenant?.pointsPriceDisplay || 'POINTS_WITH_PRICE',
    inrPerPoint:     tenant?.inrPerPoint != null ? String(tenant.inrPerPoint) : '',
    pointsManagedBy: tenant?.pointsManagedBy   || 'EYANTRA',
    pointsPartnerName: tenant?.pointsPartnerName || '',
    deliveryMode:    tenant?.deliveryMode     || 'BOTH',
    storeTagline:    tenant?.storeTagline     || '',
    deliveryEstimate: tenant?.deliveryEstimate || '',
    gtmContainerId:  tenant?.gtmContainerId   || '',
    metaPixelId:     tenant?.metaPixelId      || '',
    storeTabLabel:   tenant?.storeTabLabel    || '',
    pointsLabel:     tenant?.pointsLabel      || '',
    contactEmail:    tenant?.contactEmail     || '',
    defaultCategoryId: tenant?.defaultCategoryId != null ? String(tenant.defaultCategoryId) : '',
    faqs:            tenant?.faqs?.map(f => `${f.question} | ${f.answer}`).join('\n') || '',
    showStockPurchase: tenant?.showStockPurchase ?? false,
    voucherManagementEnabled: tenant?.voucherManagementEnabled ?? false,
    active:          tenant?.active          ?? true,
  });
  const [fonts, setFonts]     = useState(['Inter']);
  const [categories, setCategories] = useState([]);
  const [logoFile, setLogoFile]       = useState(null);
  const [logoPreview, setLogoPreview] = useState(tenant?.logoUrl || null);
  const [removingLogo, setRemovingLogo] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  // Same rule as BrandstoreVoucherService#acceptsCoupons.
  const couponsAllowed = form.paymentMode === 'MONEY' || form.paymentMode === 'POINTS_OR_MONEY';

  // Retail storefronts and Open access both lock payment to card/UPI and delivery to home.
  // Remember what was set before the lock so undoing it (Retail → Corporate, Open → Login
  // required) puts it back, instead of silently saving a points/office store as card/home.
  const beforeLock = useRef(null);
  const isLocked = (f) => f.storefrontType === 'RETAIL' || f.accessMode === 'OPEN';
  const setLockingField = (k, v) => setForm(f => {
    const next = { ...f, [k]: v };
    if (isLocked(next)) {
      if (!isLocked(f)) beforeLock.current = { paymentMode: f.paymentMode, deliveryMode: f.deliveryMode };
      return { ...next, paymentMode: 'MONEY', deliveryMode: 'HOME_ONLY' };
    }
    return isLocked(f) && beforeLock.current ? { ...next, ...beforeLock.current } : next;
  });

  useEffect(() => {
    api.get('/admin/brandstore/fonts').then(r => setFonts(r.data || ['Inter'])).catch(() => {});
    api.get('/categories').then(r => setCategories((r.data || []).slice().sort((a, b) => a.name.localeCompare(b.name)))).catch(() => {});
  }, []);

  const pickLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const removeLogo = async () => {
    if (logoFile) { setLogoFile(null); setLogoPreview(tenant?.logoUrl || null); return; }
    if (!tenant?.id) return;
    setRemovingLogo(true);
    try {
      await api.delete(`/admin/brandstore/tenants/${tenant.id}/logo`);
      setLogoPreview(null);
    } catch {
      setError('Failed to remove logo.');
    } finally {
      setRemovingLogo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    const payload = {
      ...form,
      officeLocations: form.officeLocations.split('\n').map(s => s.trim()).filter(Boolean),
      allowedEmails:   form.allowedEmails.split('\n').map(s => s.trim().toLowerCase()).filter(Boolean),
      adminEmails:     form.adminEmails.split('\n').map(s => s.trim().toLowerCase()).filter(Boolean),
      defaultCategoryId: form.defaultCategoryId ? Number(form.defaultCategoryId) : null,
      faqs: form.faqs.split('\n').map(s => s.trim()).filter(Boolean).map(line => {
        const [question, ...rest] = line.split('|');
        return { question: question.trim(), answer: rest.join('|').trim() };
      }).filter(f => f.question && f.answer),
    };
    try {
      let tenantId = tenant?.id;
      if (tenantId) {
        await api.put(`/admin/brandstore/tenants/${tenantId}`, payload);
      } else {
        const res = await api.post('/admin/brandstore/tenants', payload);
        tenantId = res.data.id;
      }
      if (logoFile) {
        const logoForm = new FormData();
        logoForm.append('logo', logoFile);
        await api.post(`/admin/brandstore/tenants/${tenantId}/logo`, logoForm);
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
      <form onSubmit={handleSubmit} style={{ background: '#fff', borderRadius: 12, padding: 28, width: 520, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <h3 style={{ fontSize: 17, fontWeight: 700, color: '#111827', margin: '0 0 20px' }}>
          {tenant ? 'Edit Company' : 'New Company'}
        </h3>

        {error && <div style={{ color: '#B91C1C', background: '#FEE2E2', borderRadius: 7, padding: '8px 12px', marginBottom: 16, fontSize: 13 }}>{error}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
          <div>
            <label style={label}>Slug (URL key) *</label>
            <input style={input} value={form.slug} onChange={e => set('slug', e.target.value)} required disabled={!!tenant} placeholder="synchrony" />
          </div>
          <div>
            <label style={label}>Display Name *</label>
            <input style={input} value={form.displayName} onChange={e => set('displayName', e.target.value)} required placeholder="Synchrony" />
          </div>
          <div>
            <label style={label}>Email Domain</label>
            <input style={input} value={form.emailDomain} onChange={e => set('emailDomain', e.target.value)} placeholder="syf.com (leave blank if using allowlist)" />
          </div>
          <div>
            <label style={label}>Primary Color</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="color" value={form.primaryColor} onChange={e => set('primaryColor', e.target.value)} style={{ width: 40, height: 36, border: '1.5px solid #E5E7EB', borderRadius: 6, padding: 2, cursor: 'pointer' }} />
              <input style={{ ...input, flex: 1 }} value={form.primaryColor} onChange={e => set('primaryColor', e.target.value)} placeholder="#F29213" />
            </div>
          </div>
          <div>
            <label style={label}>Logo Text</label>
            <input style={input} value={form.logoText} onChange={e => set('logoText', e.target.value)} placeholder="Synchrony" />
          </div>
          <div>
            <label style={label}>Font</label>
            <select style={input} value={form.fontFamily} onChange={e => set('fontFamily', e.target.value)}>
              {fonts.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={label}>Logo</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {logoPreview && (
              <img src={logoPreview} alt="" style={{ width: 44, height: 44, objectFit: 'contain', borderRadius: 6, border: '1.5px solid #E5E7EB', background: '#fff' }} />
            )}
            <input type="file" accept="image/*" onChange={pickLogo} style={{ fontSize: 12 }} />
            {logoPreview && (
              <button type="button" style={{ ...btnGhost, padding: '5px 10px', fontSize: 12 }} onClick={removeLogo} disabled={removingLogo}>
                {removingLogo ? '…' : 'Remove'}
              </button>
            )}
          </div>
          <p style={{ fontSize: 11, color: '#6B7280', margin: '5px 0 0' }}>
            Optional — falls back to Logo Text on the storefront if not set.
          </p>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={label}>Storefront Type</label>
          <select
            style={input} value={form.storefrontType}
            onChange={e => setLockingField('storefrontType', e.target.value)}
          >
            <option value="CORPORATE">Corporate — company employee / gifting store</option>
            <option value="RETAIL">Retail — exclusive-brand shop for the public</option>
          </select>
          <p style={{ fontSize: 11, color: '#6B7280', margin: '5px 0 0' }}>
            Retail stores (e.g. Rare Rabbit) get the shop experience — bag, retail wording, policy footer, no campaigns/points. Requires card/UPI payment and home delivery. Usually paired with Access Mode = Open.
          </p>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={label}>Access Mode</label>
          <select
            style={input} value={form.accessMode}
            onChange={e => setLockingField('accessMode', e.target.value)}
          >
            <option value="LOGIN_REQUIRED">Login required — company employee store</option>
            <option value="OPEN">Open — public/institutional brand store</option>
          </select>
          <p style={{ fontSize: 11, color: '#6B7280', margin: '5px 0 0' }}>
            Open stores (e.g. a licensed brand sold to institutions) let anyone browse and check out as a guest with no login — login stays optional, for order history only. Requires Payment Mode = card/UPI, since there's no company or points balance behind a guest.
          </p>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={label}>Payment Mode</label>
          <select
            style={input} value={form.paymentMode}
            onChange={e => set('paymentMode', e.target.value)}
            disabled={form.accessMode === 'OPEN' || form.storefrontType === 'RETAIL'}
          >
            <option value="COMPANY_PAID">Company pays for everything</option>
            <option value="POINTS">Employees pay with reward points</option>
            <option value="MONEY">Employees pay with card / UPI</option>
            <option value="POINTS_OR_MONEY">Employees choose — points or card/UPI</option>
          </select>
          <p style={{ fontSize: 11, color: '#6B7280', margin: '5px 0 0' }}>
            {form.accessMode === 'OPEN' || form.storefrontType === 'RETAIL'
              ? 'Locked to card/UPI while Access Mode is Open or Storefront Type is Retail.'
              : 'Anything other than "Company pays" hides the "no payment" messaging on the login page and enables checkout accordingly.'}
          </p>
        </div>

        {form.paymentMode === 'POINTS' && (
          <div style={{
            marginBottom: 14,
            padding: '12px 14px',
            borderRadius: 9,
            background: 'var(--color-primary-subtle,#FFF8EC)',
            border: '1.5px solid var(--color-primary,#F29213)',
          }}>
            <label style={label}>Show Price As</label>
            <select style={{ ...input, background: '#fff' }} value={form.pointsPriceDisplay} onChange={e => set('pointsPriceDisplay', e.target.value)}>
              <option value="POINTS_WITH_PRICE">Points with price — show the points cost + an approx. ₹ value</option>
              <option value="POINTS_ONLY">Only points — hide the ₹ value, show just the points cost</option>
            </select>
            <p style={{ fontSize: 11, color: '#6B7280', margin: '5px 0 0' }}>
              {form.pointsPriceDisplay === 'POINTS_ONLY'
                ? 'Catalog, product pages, and the order invoice show the points cost (e.g. "500 pts") without the "(≈ ₹...)" reference next to it.'
                : "Shown on the catalog card and product page. Doesn't apply to \"Employees choose — points or card/UPI\", which always shows a real figure."}
            </p>
          </div>
        )}

        {(form.paymentMode === 'POINTS' || form.paymentMode === 'POINTS_OR_MONEY') && (
          <div style={{ marginBottom: 14 }}>
            <label style={label}>Points Managed By</label>
            <select style={input} value={form.pointsManagedBy} onChange={e => set('pointsManagedBy', e.target.value)}>
              <option value="EYANTRA">eYantra (upload balances, track redemptions)</option>
              <option value="PARTNER">External partner (Bonusly, etc.)</option>
            </select>
            <p style={{ fontSize: 11, color: '#6B7280', margin: '5px 0 0' }}>
              {form.pointsManagedBy === 'PARTNER'
                ? "Checkout won't check or deduct any balance — the partner has already handled redemption. No partner integration is wired up yet; this is informational for now."
                : 'We track the balance here — upload CSVs and set the conversion rate below.'}
            </p>
          </div>
        )}

        {form.paymentMode !== 'COMPANY_PAID' && form.paymentMode !== 'MONEY' && form.pointsManagedBy === 'PARTNER' && (
          <div style={{ marginBottom: 14 }}>
            <label style={label}>Partner Name</label>
            <input style={input} value={form.pointsPartnerName} onChange={e => set('pointsPartnerName', e.target.value)} placeholder="e.g. Bonusly" />
          </div>
        )}

        {(form.paymentMode === 'POINTS' || form.paymentMode === 'POINTS_OR_MONEY') && form.pointsManagedBy === 'EYANTRA' && (
          <div style={{ marginBottom: 14 }}>
            <label style={label}>INR per Point</label>
            <input
              style={input} type="number" step="0.01" min="0.01"
              value={form.inrPerPoint} onChange={e => set('inrPerPoint', e.target.value)}
              placeholder="e.g. 0.5 (1 point = ₹0.50)"
            />
            <p style={{ fontSize: 11, color: '#6B7280', margin: '5px 0 0' }}>
              eYantra-controlled conversion rate — not editable by the company's own store admin. Manage employee point balances in the Reward Points tab.
            </p>
          </div>
        )}

        <div style={{ marginBottom: 14 }}>
          <label style={label}>Delivery</label>
          <select
            style={input} value={form.deliveryMode}
            onChange={e => set('deliveryMode', e.target.value)}
            disabled={form.accessMode === 'OPEN' || form.storefrontType === 'RETAIL'}
          >
            <option value="BOTH">Employee chooses — home or office</option>
            <option value="HOME_ONLY">Home delivery only</option>
            <option value="OFFICE_ONLY">Office delivery only</option>
          </select>
          <p style={{ fontSize: 11, color: '#6B7280', margin: '5px 0 0' }}>
            {form.accessMode === 'OPEN' || form.storefrontType === 'RETAIL'
              ? "Locked to home delivery while Access Mode is Open or Storefront Type is Retail — there's no fixed office roster for a public/institutional guest to choose from."
              : 'Controls which delivery option(s) checkout offers. Office-only stores still need at least one office location below.'}
          </p>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={label}>Office Locations (one per line)</label>
          <textarea style={{ ...input, resize: 'vertical', minHeight: 80 }} value={form.officeLocations} onChange={e => set('officeLocations', e.target.value)} placeholder={'Hyderabad - Mindspace\nBangalore - Whitefield'} />
        </div>

        <div style={{ borderTop: '1.5px solid #E5E7EB', margin: '18px 0 14px', paddingTop: 14 }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 12px' }}>
            Storefront Customization
          </p>
        </div>

        {form.storefrontType === 'RETAIL' && (
          <div style={{ marginBottom: 14 }}>
            <label style={label}>Delivery Estimate</label>
            <input style={input} value={form.deliveryEstimate} onChange={e => set('deliveryEstimate', e.target.value)} placeholder="e.g. 4–7 days" />
            <p style={{ fontSize: 11, color: '#6B7280', margin: '5px 0 0' }}>
              Shown on product pages and checkout as "Usually delivered in …". Keep it a range, never a promised date. Leave blank to show nothing.
            </p>
          </div>
        )}

        {form.storefrontType === 'RETAIL' && (
          <div style={{ marginBottom: 14 }}>
            <label style={label}>Google Tag Manager ID</label>
            <input style={input} value={form.gtmContainerId} onChange={e => set('gtmContainerId', e.target.value)} placeholder="GTM-XXXXXXX" />
            <p style={{ fontSize: 11, color: '#6B7280', margin: '5px 0 0' }}>
              Loads this store's analytics and ad tags (GA4, Google Ads), only after a shopper accepts the cookie banner. Don't add a Meta Pixel tag here — use the field below. Leave blank for no tags.
            </p>
          </div>
        )}

        {form.storefrontType === 'RETAIL' && (
          <div style={{ marginBottom: 14 }}>
            <label style={label}>Meta Pixel ID</label>
            <input style={input} value={form.metaPixelId} onChange={e => set('metaPixelId', e.target.value)} placeholder="123456789012345" inputMode="numeric" />
            <p style={{ fontSize: 11, color: '#6B7280', margin: '5px 0 0' }}>
              The number from Meta Events Manager → Data sources. Sends page views, product views, add to bag, checkout and purchases to Meta (Facebook / Instagram ads), matched to the store's product feed — only after a shopper accepts the cookie banner. Leave blank for none.
            </p>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
          <div>
            <label style={label}>Store Tagline</label>
            <input style={input} value={form.storeTagline} onChange={e => set('storeTagline', e.target.value)} placeholder={`${form.displayName || 'Acme'} Employee Store`} />
            <p style={{ fontSize: 11, color: '#6B7280', margin: '5px 0 0' }}>
              Shown on the header and login screen. Leave blank for the default "{form.displayName || 'Company'} Employee Store".
            </p>
          </div>
          <div>
            <label style={label}>Home Tab Label</label>
            <input style={input} value={form.storeTabLabel} onChange={e => set('storeTabLabel', e.target.value)} placeholder="Store" />
            <p style={{ fontSize: 11, color: '#6B7280', margin: '5px 0 0' }}>
              Label for the storefront's main tab. Defaults to "Store".
            </p>
          </div>
        </div>

        {(form.paymentMode === 'POINTS' || form.paymentMode === 'POINTS_OR_MONEY') && (
          <div style={{ marginBottom: 14 }}>
            <label style={label}>Points Label</label>
            <input style={input} value={form.pointsLabel} onChange={e => set('pointsLabel', e.target.value)} placeholder="pts" />
            <p style={{ fontSize: 11, color: '#6B7280', margin: '5px 0 0' }}>
              What to call reward points everywhere they appear (e.g. "HF Points"). Defaults to "pts".
            </p>
          </div>
        )}

        <div style={{ marginBottom: 14 }}>
          <label style={label}>Default Category</label>
          <select style={input} value={form.defaultCategoryId} onChange={e => set('defaultCategoryId', e.target.value)}>
            <option value="">All categories</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <p style={{ fontSize: 11, color: '#6B7280', margin: '5px 0 0' }}>
            The storefront opens with this category pre-selected. Shoppers can still switch categories. A campaign can set its own default category, which takes priority while shoppers are in that campaign. If the products on show have none in this category, the store opens on "All".
          </p>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={label}>Banners</label>
          {tenant?.id ? (
            <BannerManager tenantId={tenant.id} />
          ) : (
            <p style={{ fontSize: 12, color: '#6B7280', margin: 0 }}>Save the company first, then edit it to add banners.</p>
          )}
          <p style={{ fontSize: 11, color: '#6B7280', margin: '5px 0 0' }}>
            Shown above the product search on the storefront. Two or more rotate as a slideshow, in the order listed. Use wide images, ideally 1500 × 500 px (3:1), saved as JPEG or WebP and kept under 1 MB so the store loads quickly (10 MB max). Other shapes are cropped to fit. Banners aren't clickable, so don't put buttons like "Shop now" in the image. Up to 10. Changes here save immediately.
          </p>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={label}>Contact Email</label>
          <input style={input} type="email" value={form.contactEmail} onChange={e => set('contactEmail', e.target.value)} placeholder="support@company.com" />
          <p style={{ fontSize: 11, color: '#6B7280', margin: '5px 0 0' }}>
            Adds a "Contact" tab to the storefront showing this email. Leave blank to hide the tab.
          </p>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={label}>FAQs (one per line, "Question | Answer")</label>
          <textarea
            style={{ ...input, resize: 'vertical', minHeight: 90 }}
            value={form.faqs}
            onChange={e => set('faqs', e.target.value)}
            placeholder={'How long does delivery take? | Usually 5-7 business days.\nCan I change my order? | Yes, until it is confirmed.'}
          />
          <p style={{ fontSize: 11, color: '#6B7280', margin: '5px 0 0' }}>
            Adds a "FAQs" tab to the storefront. Leave blank to hide the tab.
          </p>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={label}>Allowed Emails (one per line)</label>
          <textarea style={{ ...input, resize: 'vertical', minHeight: 80 }} value={form.allowedEmails} onChange={e => set('allowedEmails', e.target.value)} placeholder={'rajeev@gmail.com\npriya@gmail.com'} />
          <p style={{ fontSize: 11, color: '#6B7280', margin: '5px 0 0' }}>
            Use this instead of Email Domain when employees have personal emails (Gmail, etc.).
            If set, only these exact addresses can log in — Email Domain is ignored.
          </p>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={label}>Store Admin Emails (one per line)</label>
          <textarea style={{ ...input, resize: 'vertical', minHeight: 70 }} value={form.adminEmails} onChange={e => set('adminEmails', e.target.value)} placeholder={'hr@synchrony.com\nops@synchrony.com'} />
          <p style={{ fontSize: 11, color: '#6B7280', margin: '5px 0 0' }}>
            These users log in via OTP and land on the admin dashboard — order tracking, status updates, and analytics.
          </p>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={label}>Enable Inventory Module?</label>
          <select
            style={input}
            value={form.showStockPurchase ? 'YES' : 'NO'}
            onChange={e => set('showStockPurchase', e.target.value === 'YES')}
          >
            <option value="NO">No</option>
            <option value="YES">Yes</option>
          </select>
          <p style={{ fontSize: 11, color: '#6B7280', margin: '5px 0 0' }}>
            Adds a "Stock" tab to the store admin dashboard: purchase entries (vendor invoices for this company's campaign products), sales, and inventory. eYantra staff get a read-only view in the Stock tab here.
          </p>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={label}>Enable Voucher Management?</label>
          <select
            style={input}
            value={form.voucherManagementEnabled ? 'YES' : 'NO'}
            onChange={e => set('voucherManagementEnabled', e.target.value === 'YES')}
          >
            <option value="NO">No</option>
            <option value="YES" disabled={!couponsAllowed}>Yes</option>
          </select>
          <p style={{ fontSize: 11, color: form.voucherManagementEnabled && !couponsAllowed ? '#B91C1C' : '#6B7280', margin: '5px 0 0' }}>
            {couponsAllowed
              ? 'Adds a "Vouchers" tab for managing employee gift vouchers. Coupons are created here in the central admin.'
              : 'Coupons are paid on the card/UPI checkout, so they need the MONEY or POINTS_OR_MONEY payment mode.'}
          </p>
        </div>

        {tenant && (
          <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" id="active" checked={form.active} onChange={e => set('active', e.target.checked)} />
            <label htmlFor="active" style={{ fontSize: 13, color: '#374151', cursor: 'pointer' }}>Active (accepting orders)</label>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
          <button type="button" style={btnGhost} onClick={onClose}>Cancel</button>
          <button type="submit" style={btnPrimary} disabled={saving}>{saving ? 'Saving…' : 'Save Company'}</button>
        </div>
      </form>
    </div>
  );
}

// ── Product assignment modal ───────────────────────────────────
function ProductAssignModal({ tenant, onClose, onSaved }) {
  const [allProducts, setAllProducts] = useState([]);
  const [selected, setSelected]       = useState(new Set());
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [search, setSearch]           = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/products', { params: { size: 500, sort: 'name,asc' } }),
      api.get(`/admin/brandstore/tenants/${tenant.id}/products`),
    ]).then(([prodRes, assignRes]) => {
      const approved = (prodRes.data?.content || prodRes.data || []).filter(p => p.approvalStatus === 'APPROVED');
      setAllProducts(approved);
      setSelected(new Set(assignRes.data || []));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [tenant.id]);

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/brandstore/tenants/${tenant.id}/products`, [...selected]);
      onSaved();
    } catch { setSaving(false); }
  };

  const filtered = allProducts.filter(p =>
    !search || p.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 620, maxWidth: '95vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Assign Products — {tenant.displayName}</h3>
            <p style={{ fontSize: 12, color: '#9CA3AF', margin: '3px 0 0' }}>{selected.size} product{selected.size !== 1 ? 's' : ''} selected — leave empty to show all approved products</p>
          </div>
          <button type="button" style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#9CA3AF' }} onClick={onClose}>✕</button>
        </div>

        <input style={{ ...input, marginBottom: 12 }} placeholder="Search products…" value={search} onChange={e => setSearch(e.target.value)} />

        <div style={{ flex: 1, overflowY: 'auto', border: '1.5px solid #E5E7EB', borderRadius: 8 }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>Loading…</div>
          ) : filtered.map(p => (
            <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderBottom: '1px solid #F3F4F6', cursor: 'pointer', background: selected.has(p.id) ? 'var(--color-primary-subtle,#FFF8EC)' : '#fff' }}>
              <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)} style={{ accentColor: 'var(--color-primary,#F29213)', width: 16, height: 16, flexShrink: 0 }} />
              {p.thumbnailUrl && <img src={p.thumbnailUrl} alt="" style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 4, flexShrink: 0 }} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', lineHeight: 1.3 }}>{p.name}</div>
                {p.esin && <div style={{ fontSize: 10, color: '#9CA3AF', fontFamily: 'monospace', fontWeight: 600, marginTop: 1 }}>{p.esin}</div>}
              </div>
              {p.brand?.name && <span style={{ marginLeft: 'auto', fontSize: 11, color: '#9CA3AF', flexShrink: 0 }}>{p.brand.name}</span>}
            </label>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center', marginTop: 14 }}>
          <button type="button" style={{ ...btnGhost, fontSize: 12 }} onClick={() => setSelected(new Set())}>Clear all</button>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" style={btnGhost} onClick={onClose}>Cancel</button>
            <button type="button" style={btnPrimary} onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : `Save (${selected.size} products)`}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Company detail panel (inline expanded) ────────────────────
const STATUS_BADGE = {
  ACTIVE: { bg: '#DCFCE7', color: '#15803D', label: 'Active' },
  PAUSED: { bg: '#FEF3C7', color: '#92400E', label: 'Paused' },
  ENDED:  { bg: '#F3F4F6', color: '#6B7280', label: 'Ended' },
};

function CompanyDetailPanel({ tenant }) {
  const { canManage, canImportCatalog } = useBrandstoreAccess();
  const navigate = useNavigate();
  const [campaigns, setCampaigns]               = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(true);
  const [editCampaign, setEditCampaign]         = useState(null);
  const [assignCampaign, setAssignCampaign]     = useState(null);
  const [assignInitialIds, setAssignInitialIds] = useState(null);

  const loadCampaigns = useCallback(() => {
    setCampaignsLoading(true);
    api.get('/admin/brandstore/campaigns', { params: { tenantId: tenant.id } })
      .then(r => { setCampaigns(r.data || []); setCampaignsLoading(false); })
      .catch(() => setCampaignsLoading(false));
  }, [tenant.id]);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <p style={{ fontSize: 12, color: '#9CA3AF', margin: 0 }}>
          Each campaign defines a product subset and optional order limit per employee.
        </p>
        {canManage && <button style={btnPrimary} onClick={() => setEditCampaign({})}>+ New Campaign</button>}
      </div>

      {campaignsLoading ? (
        <div style={{ padding: 20, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>Loading…</div>
      ) : campaigns.length === 0 ? (
        <div style={{ padding: '20px 24px', textAlign: 'center', color: '#9CA3AF', border: '1.5px dashed #E5E7EB', borderRadius: 8, fontSize: 13 }}>
          No campaigns yet. Create one to assign products to this company's employees.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {campaigns.map(c => {
            const b = STATUS_BADGE[c.status] || STATUS_BADGE.ENDED;
            return (
              <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 14, background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8, padding: '12px 16px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>{c.name}</span>
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: b.bg, color: b.color }}>{b.label}</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 3, display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    <span>{c.maxOrdersPerEmployee != null ? `Max ${c.maxOrdersPerEmployee} order${c.maxOrdersPerEmployee !== 1 ? 's' : ''}/employee` : 'Unlimited orders'}</span>
                    {c.startDate && <span>From {c.startDate}</span>}
                    {c.endDate   && <span>Until {c.endDate}</span>}
                  </div>
                </div>
                <div style={{ fontSize: 12, color: '#6B7280', textAlign: 'center', flexShrink: 0, minWidth: 48 }}>
                  <div style={{ fontWeight: 700, fontSize: 16, color: '#111827' }}>{c.productCount}</div>
                  <div>products</div>
                </div>
                {canImportCatalog && (
                  <button
                    style={{ ...btnGhost, padding: '6px 12px', fontSize: 12, flexShrink: 0 }}
                    title="Upload a supplier catalog PDF — reviewed products are added to this campaign"
                    onClick={() => navigate(`/admin?tab=CatalogImport&company=${tenant.id}&campaign=${c.id}`)}
                  >📄 Import catalog</button>
                )}
                {canManage && <div style={{ display: 'flex', gap: 7, flexShrink: 0 }}>
                  <button style={{ ...btnGhost, padding: '6px 12px', fontSize: 12 }} onClick={async () => {
                    const res = await api.get(`/admin/brandstore/campaigns/${c.id}/products`).catch(() => ({ data: [] }));
                    setAssignInitialIds(new Set(res.data || []));
                    setAssignCampaign(c);
                  }}>Assign Products</button>
                  <button style={{ ...btnGhost, padding: '6px 12px', fontSize: 12 }} onClick={() => setEditCampaign(c)}>Edit</button>
                </div>}
              </div>
            );
          })}
        </div>
      )}

      {/* Modals */}
      {editCampaign !== null && (
        <CampaignModal
          campaign={editCampaign?.id ? editCampaign : null}
          tenantId={tenant.id}
          onClose={() => setEditCampaign(null)}
          onSaved={() => { setEditCampaign(null); loadCampaigns(); }}
        />
      )}
      {assignCampaign && assignInitialIds !== null && (
        <AddProductsToCampaignModal
          campaign={assignCampaign}
          initialSelectedIds={assignInitialIds}
          onSave={async ({ selectAll, productIds }) => {
            const ids = selectAll ? [] : productIds;
            await api.put(`/admin/brandstore/campaigns/${assignCampaign.id}/products`, ids);
          }}
          onClose={() => { setAssignCampaign(null); setAssignInitialIds(null); }}
          onSuccess={() => { setAssignCampaign(null); setAssignInitialIds(null); loadCampaigns(); }}
        />
      )}
    </div>
  );
}

// ── Companies tab ─────────────────────────────────────────────
function CompaniesTab() {
  const { canManage } = useBrandstoreAccess();
  const [tenants, setTenants]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [expanded, setExpanded]     = useState(null);
  const [editTenant, setEditTenant] = useState(null);
  const [search, setSearch]         = useState('');

  const load = useCallback(() => {
    setLoading(true);
    api.get('/admin/brandstore/tenants')
      .then(r => { setTenants(r.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const normalizedSearch = search.trim().toLowerCase();
  const filteredTenants = tenants.filter(t => !normalizedSearch || [
    t.displayName, t.slug, t.emailDomain, t.contactEmail,
  ].some(value => String(value || '').toLowerCase().includes(normalizedSearch)));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: 0 }}>Companies</h2>
          <p style={{ fontSize: 13, color: '#9CA3AF', margin: '4px 0 0' }}>Each company gets its own branded employee store portal</p>
        </div>
        {canManage && <button style={btnPrimary} onClick={() => setEditTenant({})}>+ New Company</button>}
      </div>

      {!loading && tenants.length > 0 && (
        <input
          style={{ ...input, maxWidth: 360, marginBottom: 16 }}
          placeholder="Search company, slug, or domain…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#9CA3AF' }}>Loading…</div>
      ) : tenants.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#9CA3AF' }}>No companies yet. Create one to get started.</div>
      ) : filteredTenants.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#9CA3AF' }}>No companies match your search.</div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {filteredTenants.map(t => {
            const isOpen = expanded === t.id;
            return (
              <div key={t.id} style={{ border: isOpen ? '1.5px solid var(--color-primary,#F29213)' : '1.5px solid #E5E7EB', borderRadius: 12, overflow: 'hidden', transition: 'border-color 0.15s' }}>
                {/* Company header row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 20px', background: '#fff' }}>
                  {t.logoUrl ? (
                    <img src={t.logoUrl} alt="" style={{ width: 44, height: 44, objectFit: 'contain', borderRadius: 10, flexShrink: 0, background: '#fff', border: '1.5px solid #E5E7EB' }} />
                  ) : (
                    <div style={{ width: 44, height: 44, borderRadius: 10, background: t.primaryColor, flexShrink: 0, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>{t.displayName}</span>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: t.active ? '#DCFCE7' : '#F3F4F6', color: t.active ? '#15803D' : '#9CA3AF' }}>
                        {t.active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 3 }}>
                      <span style={{ marginRight: 12 }}>/{t.slug}</span>
                      {t.emailDomain && <span style={{ marginRight: 12 }}>@{t.emailDomain}</span>}
                      <span>{t.slug}.eyantra.com</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    {canManage && <button style={btnGhost} onClick={() => setEditTenant(t)}>Edit</button>}
                    <button
                      onClick={() => setExpanded(isOpen ? null : t.id)}
                      style={{ padding: '8px 16px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: isOpen ? 'var(--color-primary,#F29213)' : '#F3F4F6', color: isOpen ? '#fff' : '#374151' }}
                    >
                      {isOpen ? '▲ Close' : canManage ? '▼ Manage' : '▼ Campaigns'}
                    </button>
                  </div>
                </div>

                {/* Inline management panel */}
                {isOpen && (
                  <div style={{ borderTop: '1px solid #F3F4F6', padding: '20px 20px 24px', background: '#FAFAFA' }}>
                    <CompanyDetailPanel tenant={t} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {editTenant !== null && (
        <TenantModal
          tenant={editTenant?.id ? editTenant : null}
          onClose={() => setEditTenant(null)}
          onSaved={() => { setEditTenant(null); load(); }}
        />
      )}
    </div>
  );
}

// ── Orders tab ────────────────────────────────────────────────
// ── Order invoice modal (admin) ─────────────────────────────────
function AdminOrderInvoiceModal({ order: g, tenantName, inrPerPoint, onClose }) {
  const isPoints = g.paymentMethod === 'POINTS';
  const isMoney  = g.paymentMethod === 'MONEY';
  // Split checkout (insufficient points topped up with card/UPI) — never mixed with a
  // COMPANY_PAID group, that's a separate checkout flow entirely.
  const isSplit  = g.paymentMethod === 'POINTS_AND_MONEY';
  // Gift-voucher checkout: MONEY rows when card/UPI paid the rest, NONE when the voucher covered it all.
  // Coupon rows match the storefront invoice (brandstore-frontend CatalogPage#OrderInvoiceModal).
  const voucherInr = g.voucherCode ? Number(g.voucherAmountInr) || 0 : 0;
  const isCoupon = voucherInr > 0;
  const cardInr = Number(g.amountInr) || 0;
  const rupees = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

  // pointsCost was ceil(mrp / inrPerPoint) at order time — reversed back to an
  // approximate ₹ value, labeled "≈" since rounding means it isn't the exact MRP.
  const ptsWithMrp = (pts) => {
    const label = `${(pts || 0).toLocaleString('en-IN')} pts`;
    return inrPerPoint ? `${label} (≈ ₹${Math.round((pts || 0) * Number(inrPerPoint)).toLocaleString('en-IN')})` : label;
  };

  const row = (lbl, value) => value == null || value === '' ? null : (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0', fontSize: 13 }}>
      <span style={{ color: '#9CA3AF' }}>{lbl}</span>
      <span style={{ color: '#111827', fontWeight: 600, textAlign: 'right' }}>{value}</span>
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }}>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #admin-invoice-print-area, #admin-invoice-print-area * { visibility: visible; }
          #admin-invoice-print-area { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); max-height: none; box-shadow: none; }
          [data-no-print] { display: none !important; }
        }
      `}</style>
      <div id="admin-invoice-print-area" style={{ background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 520, maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Invoice · {tenantName}</div>
            <h3 style={{ fontSize: 18, fontWeight: 800, color: '#111827', margin: '2px 0 0' }}>Order #{g.id}</h3>
            <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 3 }}>
              {g.partnerOrder
                ? <><strong style={{ color: '#374151' }}>{g.recipientName}</strong>{g.employeeEmail ? ` (${g.employeeEmail})` : ''} · Partner order {g.externalRef}</>
                : g.employeeName ? <><strong style={{ color: '#374151' }}>{g.employeeName}</strong> ({g.employeeEmail})</> : g.employeeEmail}
            </div>
            <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 1 }}>
              {fmtIstDateTime(g.createdAt, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={pill(g.status)}>{g.status.replace(/_/g, ' ')}</span>
            <button
              onClick={() => {
                const customer = g.partnerOrder ? g.externalRef : (g.employeeName || (g.employeeEmail || '').split('@')[0] || 'Customer');
                printWithFilename(`${sanitizeForFilename(tenantName)}-${sanitizeForFilename(customer)}-Order${g.id}`);
              }}
              title="Print invoice" data-no-print style={{ background: 'none', border: '1.5px solid #E5E7EB', borderRadius: 6, padding: '4px 9px', fontSize: 14, cursor: 'pointer', color: '#6B7280' }}
            >🖨️</button>
            <button onClick={onClose} data-no-print style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#9CA3AF' }}>✕</button>
          </div>
        </div>

        {g.campaignName && (
          <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 14 }}>🎯 Campaign: <strong>{g.campaignName}</strong></div>
        )}

        <div style={{ border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden', marginBottom: 18 }}>
          <div style={{ background: '#F9FAFB', padding: '8px 14px', fontSize: 11, fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.5px', textTransform: 'uppercase', display: 'flex', justifyContent: 'space-between' }}>
            <span>Item</span>
            <span>Price</span>
          </div>
          {g.products.map(p => {
            const variantLabel = [p.selectedSize, p.selectedColor].filter(Boolean).join(' · ');
            const price = isCoupon
                        ? (Number(p.voucherAmountInr) > 0
                            ? `${rupees(p.voucherAmountInr)} coupon${Number(p.amountInr) > 0 ? ` + ${rupees(p.amountInr)}` : ''}`
                            : Number(p.amountInr) > 0 ? rupees(p.amountInr) : '—')
                        : p.pointsCost != null && p.amountInr != null
                        ? `${ptsWithMrp(p.pointsCost)} + ₹${Number(p.amountInr).toLocaleString('en-IN')}`
                        : p.pointsCost != null ? ptsWithMrp(p.pointsCost)
                        : p.amountInr != null ? `₹${Number(p.amountInr).toLocaleString('en-IN')}` : '—';
            return (
              <div key={p.orderId} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '10px 14px', borderTop: '1px solid #F3F4F6' }}>
                {p.productImageUrl && <img src={p.productImageUrl} alt="" style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 6, background: '#F9FAFB', flexShrink: 0 }} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{p.productName}</div>
                  <div style={{ fontSize: 11, color: '#9CA3AF' }}>{variantLabel && `${variantLabel} · `}{p.sku && `SKU: ${p.sku} · `}Qty {p.quantity ?? 1}</div>
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', whiteSpace: 'nowrap' }}>{price}</div>
              </div>
            );
          })}
        </div>

        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 6 }}>
            {g.deliveryType === 'HOME' ? '🏠 Delivery Address' : '🏢 Office Address'}
          </div>
          {g.deliveryType === 'HOME' ? (
            <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.7 }}>
              <div style={{ fontWeight: 600 }}>{g.recipientName}</div>
              <div>{g.phone}{g.alternatePhone ? ` / ${g.alternatePhone}` : ''}</div>
              <div>{g.addressLine1}{g.addressLine2 ? `, ${g.addressLine2}` : ''}</div>
              <div>{g.city}, {g.state} — {g.pincode}</div>
            </div>
          ) : (
            <div style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>{g.officeLocation}</div>
          )}
          {g.deliveryDate && (
            <div style={{ fontSize: 12, color: '#6B7280', marginTop: 6 }}>
              Expected delivery: <strong style={{ color: '#374151' }}>{new Date(g.deliveryDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</strong>
            </div>
          )}
        </div>

        <div style={{ background: '#F9FAFB', borderRadius: 10, padding: '12px 14px', marginBottom: 8 }}>
          {(isPoints || isSplit) && row('Redeemed reward points', ptsWithMrp(g.pointsCost))}
          {isCoupon && row('Coupon applied', <><CouponChip code={g.voucherCode} /> <span style={{ marginLeft: 6 }}>{rupees(voucherInr)}</span></>)}
          {isCoupon && cardInr > 0 && row('Paid by card/UPI', rupees(cardInr))}
          {(isMoney || isSplit) && row('Razorpay Order ID', g.razorpayOrderId)}
          {(isMoney || isSplit) && row('Razorpay Payment ID', g.razorpayPaymentId)}
          {(isMoney || isSplit) && row('Payment Status', g.paymentStatus)}
          {(isMoney || isSplit) && g.razorpayRefundId && row('Razorpay Refund ID', g.razorpayRefundId)}
          {g.refundNeedsRetry && (
            <div style={{ fontSize: 12, color: '#B91C1C', fontWeight: 700, marginTop: 4 }}>
              ⚠ Refund failed — use Retry Refund on the Orders tab
            </div>
          )}
          {!isPoints && !isMoney && !isSplit && row('Billable to', tenantName)}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 8, marginTop: 6, borderTop: '1px solid #E5E7EB' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>Order Total</span>
            <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--color-primary,#F29213)' }}>
              {isSplit ? `${ptsWithMrp(g.pointsCost)} + ₹${Number(g.amountInr || 0).toLocaleString('en-IN')}`
                : isPoints ? ptsWithMrp(g.pointsCost)
                : isCoupon ? rupees(voucherInr + cardInr)
                : isMoney ? `₹${Number(g.amountInr || 0).toLocaleString('en-IN')}`
                : g.amountInr != null ? `₹${Number(g.amountInr).toLocaleString('en-IN')}` : '—'}
            </span>
          </div>
          {!isPoints && !isMoney && !isSplit && g.amountInr == null && !isCoupon && (
            <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 6, fontStyle: 'italic' }}>
              Price wasn't captured for this order (placed before pricing was tracked on company-sponsored orders).
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Billing tab ───────────────────────────────────────────────
// On-demand company invoicing. See BrandstoreOrder.billableAmountInr on the backend for
// exactly what counts as billable (COMPANY_PAID/partner-managed orders at full value,
// eYantra-managed POINTS orders at their cash value, only the points portion of a split
// checkout — the money portion was already collected directly from the employee).
function formatFileSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function InvoiceDetailModal({ invoiceId, tenantName, onClose, onStatusChanged }) {
  const { canManage } = useBrandstoreAccess();
  const { addToast } = useToast();
  const confirm = useConfirm();
  const [invoice, setInvoice] = useState(null);
  const [notes, setNotes]     = useState('');
  const [amountPaid, setAmountPaid] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [saving, setSaving]   = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/brandstore/invoices/${invoiceId}`)
      .then(r => { setInvoice(r.data); setNotes(r.data.notes || ''); })
      .catch(() => {});
  }, [invoiceId]);

  useEffect(() => { load(); }, [load]);

  const amountMatches = invoice && amountPaid !== '' &&
    Number(amountPaid).toFixed(2) === Number(invoice.totalAmountInr || 0).toFixed(2);

  const markPaid = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/brandstore/invoices/${invoiceId}/status`, {
        status: 'PAID', notes, amountPaidInr: amountPaid, paymentReference,
      });
      setAmountPaid(''); setPaymentReference('');
      load();
      onStatusChanged?.();
      addToast('Invoice marked as paid.', 'success');
    } catch (err) {
      addToast(err.response?.data?.error || 'Failed to update invoice status.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const markUnpaid = async () => {
    const ok = await confirm({
      title: 'Revert this invoice to unpaid?',
      message: 'The recorded payment reference will be cleared.',
      confirmLabel: 'Revert to unpaid',
      tone: 'danger',
    });
    if (!ok) return;
    setSaving(true);
    try {
      await api.put(`/admin/brandstore/invoices/${invoiceId}/status`, { status: 'UNPAID', notes });
      load();
      onStatusChanged?.();
      addToast('Invoice reverted to unpaid.', 'success');
    } catch (err) {
      addToast(err.response?.data?.error || 'Failed to update invoice status.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setUploading(true);
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    try {
      await api.post(`/admin/brandstore/invoices/${invoiceId}/attachments`, formData);
      load();
      addToast(files.length === 1 ? 'Proof of payment uploaded.' : `${files.length} files uploaded.`, 'success');
    } catch {
      addToast('Failed to upload proof of payment.', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    const ok = await confirm({
      title: 'Remove this document?',
      message: 'The proof of payment will be deleted from this invoice.',
      confirmLabel: 'Remove',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/brandstore/invoices/${invoiceId}/attachments/${attachmentId}`);
      load();
      addToast('Document removed.', 'success');
    } catch {
      addToast('Failed to remove document.', 'error');
    }
  };

  if (!invoice) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }}>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #invoice-bill-print-area, #invoice-bill-print-area * { visibility: visible; }
          #invoice-bill-print-area { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); max-height: none; box-shadow: none; }
          [data-no-print] { display: none !important; }
        }
      `}</style>
      <div id="invoice-bill-print-area" style={{ background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 620, maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Invoice · {tenantName}</div>
            <h3 style={{ fontSize: 18, fontWeight: 800, color: '#111827', margin: '2px 0 0' }}>{invoice.invoiceNumber}</h3>
            <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 3 }}>
              {new Date(invoice.periodStart).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
              {' – '}
              {new Date(invoice.periodEnd).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: invoice.status === 'PAID' ? '#DCFCE7' : '#FEF3C7', color: invoice.status === 'PAID' ? '#15803D' : '#B45309', whiteSpace: 'nowrap' }}>
              {invoice.status}
            </span>
            <button
              onClick={() => printWithFilename(`${sanitizeForFilename(tenantName)}-Invoice-${invoice.invoiceNumber}`)}
              title="Print invoice" data-no-print style={{ background: 'none', border: '1.5px solid #E5E7EB', borderRadius: 6, padding: '4px 9px', fontSize: 14, cursor: 'pointer', color: '#6B7280' }}
            >🖨️</button>
            <button onClick={onClose} data-no-print style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#9CA3AF' }}>✕</button>
          </div>
        </div>

        <div style={{ border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden', marginBottom: 18 }}>
          <div style={{ background: '#F9FAFB', padding: '8px 14px', fontSize: 11, fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.5px', textTransform: 'uppercase', display: 'flex', justifyContent: 'space-between' }}>
            <span>Order · Employee</span>
            <span>Amount</span>
          </div>
          {(invoice.lineItems || []).map(li => (
            <div key={li.orderId} style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderTop: '1px solid #F3F4F6' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>#{li.orderId} — {li.productName}</div>
                <div style={{ fontSize: 11, color: '#9CA3AF' }}>{li.externalRef ? `Partner order ${li.externalRef}` : li.employeeEmail} · {fmtIstDate(li.createdAt)}</div>
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', whiteSpace: 'nowrap' }}>₹{Number(li.billableAmountInr || 0).toLocaleString('en-IN')}</div>
            </div>
          ))}
          {(invoice.returnCredits || []).map(rc => (
            <div key={rc.returnRef} style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderTop: '1px solid #F3F4F6' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>Return {rc.returnRef} — order {rc.externalRef}</div>
                <div style={{ fontSize: 11, color: '#9CA3AF' }}>Received {fmtIstDate(rc.receivedAt)}</div>
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#15803D', whiteSpace: 'nowrap' }}>−₹{Number(rc.creditAmountInr || 0).toLocaleString('en-IN')}</div>
            </div>
          ))}
        </div>

        <div style={{ background: '#F9FAFB', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#6B7280', marginBottom: 4 }}>
            <span>{invoice.orderCount} order{invoice.orderCount === 1 ? '' : 's'}</span>
            {invoice.returnCreditInr != null && (
              <span>
                ₹{(Number(invoice.totalAmountInr || 0) + Number(invoice.returnCreditInr)).toLocaleString('en-IN')} less {invoice.returnCount} return credit{invoice.returnCount === 1 ? '' : 's'} ₹{Number(invoice.returnCreditInr).toLocaleString('en-IN')}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>Amount Due</span>
            <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--color-primary,#F29213)' }}>₹{Number(invoice.totalAmountInr || 0).toLocaleString('en-IN')}</span>
          </div>
        </div>

        <div data-no-print>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <label style={{ ...label, marginBottom: 0 }}>Proof of Payment</label>
            {canManage && <label style={{ ...btnGhost, padding: '4px 10px', fontSize: 11, display: 'inline-flex', alignItems: 'center', cursor: uploading ? 'default' : 'pointer', opacity: uploading ? 0.6 : 1 }}>
              {uploading ? 'Uploading…' : '⬆ Upload documents'}
              <input type="file" multiple onChange={handleFilesSelected} disabled={uploading} style={{ display: 'none' }} />
            </label>}
          </div>
          {(invoice.attachments || []).length === 0 ? (
            <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 14 }}>No documents uploaded yet.</div>
          ) : (
            <div style={{ border: '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden', marginBottom: 14 }}>
              {invoice.attachments.map((a, i) => (
                <div key={a.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '8px 12px', borderTop: i > 0 ? '1px solid #F3F4F6' : 'none', background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                  <a href={a.url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#374151', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }} title={a.fileName}>
                    📎 {a.fileName} <span style={{ color: '#9CA3AF' }}>({formatFileSize(a.fileSizeBytes)})</span>
                  </a>
                  {canManage && <button onClick={() => handleDeleteAttachment(a.id)} style={{ background: 'none', border: 'none', color: '#B91C1C', cursor: 'pointer', fontSize: 12, flexShrink: 0 }}>Remove</button>}
                </div>
              ))}
            </div>
          )}

          {!canManage ? (
            <div style={{ fontSize: 12, color: invoice.status === 'PAID' ? '#15803D' : '#B45309', fontWeight: 600 }}>
              {invoice.status === 'PAID'
                ? `Paid by ${invoice.paidBy || 'unknown'}${invoice.paymentReference ? ` · Ref ${invoice.paymentReference}` : ''}`
                : 'Unpaid'}
            </div>
          ) : invoice.status === 'PAID' ? (
            <>
              <div style={{ background: '#DCFCE7', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#15803D', marginBottom: 12, lineHeight: 1.7 }}>
                <strong>Paid</strong> by {invoice.paidBy || 'unknown'} on {invoice.paidAt ? new Date(invoice.paidAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                <br />Reference: <strong>{invoice.paymentReference || '—'}</strong>
              </div>
              <label style={label}>Notes</label>
              <textarea style={{ ...input, minHeight: 50, marginBottom: 12, resize: 'vertical' }} value={notes} onChange={e => setNotes(e.target.value)} />
              <button onClick={markUnpaid} disabled={saving} style={{ ...btnGhost, width: '100%', opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving…' : 'Mark as Unpaid'}
              </button>
            </>
          ) : (
            <>
              {(invoice.attachments || []).length === 0 && (
                <div style={{ fontSize: 12, color: '#B45309', marginBottom: 10 }}>
                  Upload at least one proof-of-payment document above before this can be marked paid.
                </div>
              )}
              <label style={label}>Amount Received (₹)</label>
              <input
                type="number" step="0.01" style={{ ...input, marginBottom: amountPaid !== '' && !amountMatches ? 4 : 12 }}
                value={amountPaid} onChange={e => setAmountPaid(e.target.value)}
                placeholder={Number(invoice.totalAmountInr || 0).toFixed(2)}
              />
              {amountPaid !== '' && !amountMatches && (
                <div style={{ fontSize: 11, color: '#B91C1C', marginBottom: 10 }}>
                  Must exactly match the invoice total (₹{Number(invoice.totalAmountInr || 0).toLocaleString('en-IN')}).
                </div>
              )}
              <label style={label}>Payment Reference (UTR / cheque no. / transaction ID)</label>
              <input style={{ ...input, marginBottom: 12 }} value={paymentReference} onChange={e => setPaymentReference(e.target.value)} />
              <label style={label}>Notes (optional)</label>
              <textarea style={{ ...input, minHeight: 50, marginBottom: 12, resize: 'vertical' }} value={notes} onChange={e => setNotes(e.target.value)} />
              <button
                onClick={markPaid}
                disabled={saving || (invoice.attachments || []).length === 0 || !amountMatches || !paymentReference.trim()}
                style={{ ...btnPrimary, width: '100%', opacity: (saving || (invoice.attachments || []).length === 0 || !amountMatches || !paymentReference.trim()) ? 0.5 : 1 }}
              >
                {saving ? 'Saving…' : 'Mark as Paid'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function BillingTab({ tenants }) {
  const { canManage } = useBrandstoreAccess();
  const [tenantId, setTenantId]   = useState('');
  const [preview, setPreview]     = useState(null);
  const [from, setFrom]           = useState('');
  const [to, setTo]               = useState('');
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError]   = useState('');
  const [invoices, setInvoices]   = useState([]);
  const [page, setPage]           = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [viewInvoiceId, setViewInvoiceId] = useState(null);

  const tenant = tenants.find(t => String(t.id) === String(tenantId));

  const loadPreview = useCallback(() => {
    if (!tenantId) return;
    api.get(`/admin/brandstore/tenants/${tenantId}/invoices/preview`, { params: { from: from || undefined, to: to || undefined } })
      .then(r => {
        setPreview(r.data);
        if (!from) setFrom(r.data.suggestedFrom);
        if (!to) setTo(r.data.suggestedTo);
      })
      .catch(() => setPreview(null));
  }, [tenantId, from, to]);

  const loadInvoices = useCallback(() => {
    if (!tenantId) { setInvoices([]); setTotalPages(0); return; }
    api.get(`/admin/brandstore/tenants/${tenantId}/invoices`, { params: { page, size: 10 } })
      .then(r => { setInvoices(r.data.content || []); setTotalPages(r.data.totalPages || 0); })
      .catch(() => {});
  }, [tenantId, page]);

  useEffect(() => { setFrom(''); setTo(''); setPreview(null); setPage(0); }, [tenantId]);
  useEffect(() => { loadPreview(); }, [loadPreview]);
  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  const handleGenerate = async () => {
    if (!from || !to) return;
    setGenerating(true);
    setGenError('');
    try {
      await api.post(`/admin/brandstore/tenants/${tenantId}/invoices/generate`, { from, to });
      setFrom(''); setTo('');
      loadPreview();
      loadInvoices();
    } catch (err) {
      setGenError(err.response?.data?.error || 'Failed to generate invoice.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: 0 }}>Billing</h2>
        <p style={{ fontSize: 13, color: '#9CA3AF', margin: '4px 0 0' }}>
          Generate on-demand invoices covering everything owed to eYantra since the last bill — company-sponsored orders at full value, and the points portion of eYantra-managed reward-point orders (a split checkout's card/UPI portion was already collected directly from the employee, so it's never billed here).
        </p>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={tenantId} onChange={e => setTenantId(e.target.value)} style={{ ...input, width: 'auto', padding: '8px 10px' }}>
          <option value="">Select company…</option>
          {tenants.map(t => <option key={t.id} value={t.id}>{t.displayName}</option>)}
        </select>
      </div>

      {!tenantId ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#9CA3AF' }}>Select a company to view billing.</div>
      ) : (
        <>
          <div style={card}>
            {preview?.lastInvoice ? (
              <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 14 }}>
                Last invoice <strong style={{ color: '#111827' }}>{preview.lastInvoice.invoiceNumber}</strong> generated{' '}
                {new Date(preview.lastInvoice.generatedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })},
                covering orders through {new Date(preview.lastInvoice.periodEnd).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                {' — '}
                <span style={{ color: preview.lastInvoice.status === 'PAID' ? '#15803D' : '#B45309', fontWeight: 700 }}>{preview.lastInvoice.status}</span>
              </div>
            ) : (
              <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 14 }}>No invoices generated yet for this company.</div>
            )}

            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 14 }}>
              <div>
                <label style={label}>From</label>
                <input type="date" style={{ ...input, width: 'auto' }} value={from} onChange={e => setFrom(e.target.value)} />
              </div>
              <div>
                <label style={label}>To</label>
                <input type="date" style={{ ...input, width: 'auto' }} value={to} onChange={e => setTo(e.target.value)} />
              </div>
              <button onClick={loadPreview} style={btnGhost}>↻ Update preview</button>
            </div>

            {preview && (
              <div style={{ background: 'var(--color-primary-subtle,#FFF3E0)', borderRadius: 8, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <span style={{ fontSize: 13, color: '#374151' }}>
                  {preview.unbilledOrderCount} unbilled order{preview.unbilledOrderCount === 1 ? '' : 's'} in this range
                  {preview.returnCount > 0 && (
                    <> · ₹{Number(preview.unbilledTotalInr || 0).toLocaleString('en-IN')} less {preview.returnCount} partner return credit{preview.returnCount === 1 ? '' : 's'} (₹{Number(preview.returnCreditInr || 0).toLocaleString('en-IN')})</>
                  )}
                </span>
                <span style={{ fontSize: 20, fontWeight: 800, color: 'var(--color-primary,#F29213)' }}>₹{Number(preview.netPayableInr ?? preview.unbilledTotalInr ?? 0).toLocaleString('en-IN')}</span>
              </div>
            )}

            {genError && <div style={{ color: '#B91C1C', fontSize: 13, marginBottom: 10 }}>{genError}</div>}

            {canManage && <button
              onClick={handleGenerate}
              disabled={generating || !preview || (preview.unbilledOrderCount === 0 && !preview.returnCount)}
              style={{ ...btnPrimary, opacity: (generating || !preview || (preview.unbilledOrderCount === 0 && !preview.returnCount)) ? 0.5 : 1 }}
            >
              {generating ? 'Generating…' : `Generate Invoice for ${tenant?.displayName || 'company'}`}
            </button>}
          </div>

          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#111827', margin: '24px 0 12px' }}>Past Invoices</h3>
          {invoices.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>No invoices yet.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <div style={{ background: '#fff', borderRadius: 12, border: '1.5px solid #E5E7EB', overflow: 'hidden', minWidth: 640 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#F9FAFB', borderBottom: '1.5px solid #E5E7EB' }}>
                      {['Invoice #', 'Period', 'Orders', 'Amount', 'Status', 'Generated', ''].map(h => (
                        <th key={h} style={{ padding: '11px 13px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((inv, idx) => (
                      <tr key={inv.id} style={{ borderBottom: '1px solid #F3F4F6', background: idx % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                        <td style={{ padding: '10px 13px', fontSize: 12, fontWeight: 700, color: 'var(--color-primary,#F29213)' }}>{inv.invoiceNumber}</td>
                        <td style={{ padding: '10px 13px', fontSize: 12, color: '#6B7280', whiteSpace: 'nowrap' }}>
                          {new Date(inv.periodStart).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} – {new Date(inv.periodEnd).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </td>
                        <td style={{ padding: '10px 13px', fontSize: 12, color: '#374151' }}>{inv.orderCount}</td>
                        <td style={{ padding: '10px 13px', fontSize: 13, fontWeight: 700, color: '#111827' }}>₹{Number(inv.totalAmountInr || 0).toLocaleString('en-IN')}</td>
                        <td style={{ padding: '10px 13px' }}>
                          <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: inv.status === 'PAID' ? '#DCFCE7' : '#FEF3C7', color: inv.status === 'PAID' ? '#15803D' : '#B45309' }}>
                            {inv.status}
                          </span>
                        </td>
                        <td style={{ padding: '10px 13px', fontSize: 12, color: '#9CA3AF', whiteSpace: 'nowrap' }}>
                          {new Date(inv.generatedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </td>
                        <td style={{ padding: '10px 13px' }}>
                          <button onClick={() => setViewInvoiceId(inv.id)} style={{ ...btnGhost, padding: '4px 10px', fontSize: 11 }}>View</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginTop: 14 }}>
                  <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={{ ...btnGhost, padding: '6px 14px', fontSize: 12, opacity: page === 0 ? 0.5 : 1 }}>← Prev</button>
                  <span style={{ fontSize: 12, color: '#9CA3AF' }}>Page {page + 1} of {totalPages}</span>
                  <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} style={{ ...btnGhost, padding: '6px 14px', fontSize: 12, opacity: page >= totalPages - 1 ? 0.5 : 1 }}>Next →</button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {viewInvoiceId && (
        <InvoiceDetailModal invoiceId={viewInvoiceId} tenantName={tenant?.displayName} onClose={() => setViewInvoiceId(null)} onStatusChanged={loadInvoices} />
      )}
    </div>
  );
}

function OrdersTab({ tenants }) {
  const { addToast } = useToast();
  const confirm = useConfirm();
  const [groups, setGroups]               = useState([]);
  const [loading, setLoading]             = useState(true);
  const [filterTenant, setFilterTenant]   = useState('');
  const [filterCampaign, setFilterCampaign] = useState('');
  const [filterStatus, setFilterStatus]   = useState('ALL');
  const [search, setSearch]               = useState('');
  const [advancing, setAdvancing]         = useState(null);
  const [expanded, setExpanded]           = useState(null);
  const [invoiceGroup, setInvoiceGroup]   = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = filterTenant ? { tenantId: filterTenant } : {};
    api.get('/admin/brandstore/orders', { params })
      .then(r => { setGroups(r.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [filterTenant]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setFilterCampaign(''); }, [filterTenant]);

  // Unique campaign names from loaded groups
  const campaigns = React.useMemo(() => {
    const names = groups.map(g => g.campaignName).filter(Boolean);
    return [...new Set(names)].sort();
  }, [groups]);

  const tenantName = (id) => tenants.find(t => t.id === id)?.displayName || `#${id}`;
  const normalizedSearch = search.trim().toLowerCase();
  const matchesSearch = (group) => !normalizedSearch || [
    group.id,
    tenantName(group.tenantId),
    group.employeeName,
    group.employeeEmail,
    group.externalRef,
    group.campaignName,
    group.voucherCode,
    group.recipientName,
    group.phone,
    ...(group.products || []).flatMap(product => [product.productName, product.sku, product.selectedSize, product.selectedColor]),
  ].some(value => String(value || '').toLowerCase().includes(normalizedSearch));

  // Apply campaign filter, then status filter
  const campaignFiltered = filterCampaign
    ? groups.filter(g => g.campaignName === filterCampaign)
    : groups;
  const searchFiltered = campaignFiltered.filter(matchesSearch);

  const counts = STATUS_ORDER.reduce((acc, s) => {
    acc[s] = searchFiltered.filter(g => g.status === s).length;
    return acc;
  }, {});
  const filtered = filterStatus === 'ALL' ? searchFiltered : searchFiltered.filter(g => g.status === filterStatus);

  const updateStatus = async (group, status) => {
    const label = group.products.length > 1
      ? `this order (${group.products.length} products)`
      : `order #${group.id}`;
    // Confirming an order requires the admin to set a delivery date on the spot —
    // it's carried into the "Order Confirmed" email (see EmailService).
    const isConfirming = status === 'CONFIRMED';
    const outcome = await confirm({
      title: `Move ${label} to ${status.replace(/_/g, ' ').toLowerCase()}?`,
      confirmLabel: isConfirming ? 'Confirm order' : 'Move',
      ...(isConfirming && {
        message: 'Set the expected delivery date — it will be included in the order confirmation email.',
        requireDate: true,
        dateLabel: 'Delivery date',
      }),
    });
    if (!outcome) return;
    const body = isConfirming ? { status, deliveryDate: outcome.date } : { status };
    setAdvancing(group.groupToken || group.id);
    try {
      if (group.groupToken) {
        await api.put(`/admin/brandstore/orders/group/${group.groupToken}/status`, body);
      } else {
        await api.put(`/admin/brandstore/orders/${group.id}/status`, body);
      }
      load();
      addToast(`Moved to ${status.replace(/_/g, ' ').toLowerCase()}.`, 'success');
    } catch { addToast('Failed to update status.', 'error'); }
    finally { setAdvancing(null); }
  };

  // Resubmitting the order's own current status re-runs RewardPaymentService's refund
  // logic (idempotent, guarded by razorpayRefundId == null) — this is the retry path for
  // a Razorpay refund call that failed when the order was first cancelled/returned.
  const retryRefund = async (group) => {
    const ok = await confirm({
      title: `Retry the Razorpay refund for order #${group.id}?`,
      message: 'This re-runs the refund against Razorpay. It is guarded against double-refunding, but it does move money.',
      confirmLabel: 'Retry refund',
      tone: 'danger',
    });
    if (!ok) return;
    setAdvancing(group.groupToken || group.id);
    try {
      if (group.groupToken) {
        await api.put(`/admin/brandstore/orders/group/${group.groupToken}/status`, { status: group.status });
      } else {
        await api.put(`/admin/brandstore/orders/${group.id}/status`, { status: group.status });
      }
      load();
      // Deliberately not "refund issued" — this re-runs the refund path, and the
      // response doesn't tell us whether Razorpay actually settled it this time.
      addToast('Refund retry submitted. Check the order for the updated refund status.', 'info', 5000);
    } catch { addToast('Failed to retry refund.', 'error'); }
    finally { setAdvancing(null); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          style={{ ...input, width: 360, padding: '8px 10px' }}
          placeholder="Search order, employee, product, SKU, coupon…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select value={filterTenant} onChange={e => setFilterTenant(e.target.value)} style={{ ...input, width: 'auto', padding: '8px 10px' }}>
          <option value="">All Companies</option>
          {tenants.map(t => <option key={t.id} value={t.id}>{t.displayName}</option>)}
        </select>
        {campaigns.length > 0 && (
          <select value={filterCampaign} onChange={e => setFilterCampaign(e.target.value)} style={{ ...input, width: 'auto', padding: '8px 10px' }}>
            <option value="">All Campaigns</option>
            {campaigns.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[{ key: 'ALL', label: 'All', count: searchFiltered.length },
            ...STATUS_ORDER.map(s => ({ key: s, label: s.replace(/_/g, ' '), count: counts[s] || 0 }))
          ].map(tab => (
            <button key={tab.key} onClick={() => setFilterStatus(tab.key)} style={{
              padding: '7px 14px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
              background: filterStatus === tab.key ? 'var(--color-primary,#F29213)' : '#fff',
              color: filterStatus === tab.key ? '#fff' : '#4B5563',
              boxShadow: filterStatus === tab.key ? 'none' : '0 1px 4px rgba(0,0,0,0.08)',
            }}>
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>
        <button onClick={load} style={{ marginLeft: 'auto', ...btnGhost }}>↻ Refresh</button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#9CA3AF' }}>Loading orders…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#9CA3AF' }}>No orders found.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
        <div style={{ background: '#fff', borderRadius: 12, border: '1.5px solid #E5E7EB', overflow: 'hidden', minWidth: 820 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#F9FAFB', borderBottom: '1.5px solid #E5E7EB' }}>
                {['#', 'Company', 'Employee', 'Products', 'Campaign', 'Type', 'Status', 'Date', 'Action'].map(h => (
                  <th key={h} style={{ padding: '11px 13px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((g, idx) => {
                const rowKey   = g.groupToken || `solo_${g.id}`;
                const isExp    = expanded === rowKey;
                const isAdv    = advancing === (g.groupToken || g.id);
                const next     = STATUS_NEXT[g.status];
                const isGroup  = g.products.length > 1;
                return (
                  <React.Fragment key={rowKey}>
                    <tr onClick={() => setExpanded(isExp ? null : rowKey)} style={{ cursor: 'pointer', borderBottom: isExp ? 'none' : '1px solid #F3F4F6', background: isExp ? '#FFF8EC' : idx % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                      <td style={{ padding: '11px 13px', fontSize: 13, fontWeight: 700, color: 'var(--color-primary,#F29213)', whiteSpace: 'nowrap' }}>
                        #{g.id}
                        {isGroup && <span style={{ marginLeft: 4, fontSize: 10, background: '#FFF8EC', color: '#D97D0A', borderRadius: 10, padding: '1px 6px', fontWeight: 700 }}>+{g.products.length - 1}</span>}
                        {g.partnerOrder && (
                          <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 3 }}>
                            <span title="Placed by the reseller partner through the API" style={{ fontSize: 10, background: '#EFF6FF', color: '#1D4ED8', borderRadius: 10, padding: '1px 7px', fontWeight: 700 }}>
                              Partner · {g.externalRef}
                            </span>
                            {g.returnCount > 0 && (
                              <span title="See the Partner Returns tab" style={{ fontSize: 10, background: '#FEE2E2', color: '#B91C1C', borderRadius: 10, padding: '1px 7px', fontWeight: 700 }}>
                                ↩ {g.returnCount} return{g.returnCount === 1 ? '' : 's'}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '11px 13px', fontSize: 12, color: '#6B7280' }}>{tenantName(g.tenantId)}</td>
                      <td style={{ padding: '11px 13px', fontSize: 12, color: '#374151', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {g.partnerOrder
                          ? <>{g.recipientName}{g.employeeEmail && <div style={{ color: '#9CA3AF', overflow: 'hidden', textOverflow: 'ellipsis' }}>{g.employeeEmail}</div>}</>
                          : g.employeeEmail}
                        {/* Highlighted so it's easy to see who used a coupon (also searchable by code). */}
                        {g.voucherCode && (
                          <div><CouponChip code={g.voucherCode} /></div>
                        )}
                      </td>
                      <td style={{ padding: '11px 13px', fontSize: 12, color: '#374151', maxWidth: 180 }}>
                        {isGroup ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {g.products.map(p => {
                              const variant = [p.selectedSize, p.selectedColor].filter(Boolean).join('/');
                              return (
                                <div key={p.orderId} style={{ fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  · {p.productName}{variant ? <span style={{ opacity: 0.6 }}> ({variant})</span> : ''}{p.sku ? <span style={{ opacity: 0.5 }}> · {p.sku}</span> : ''}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div>
                            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.products[0]?.productName}</div>
                            {(() => {
                              const v = [g.products[0]?.selectedSize, g.products[0]?.selectedColor].filter(Boolean).join('/');
                              const sku = g.products[0]?.sku;
                              return (v || sku) ? (
                                <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 1 }}>
                                  {v}{v && sku ? ' · ' : ''}{sku || ''}
                                </div>
                              ) : null;
                            })()}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '11px 13px', fontSize: 12 }}>
                        {g.campaignName
                          ? <span style={{ background: '#FFF8EC', color: '#D97D0A', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, whiteSpace: 'nowrap' }}>{g.campaignName}</span>
                          : <span style={{ color: '#D1D5DB' }}>—</span>}
                      </td>
                      <td style={{ padding: '11px 13px', fontSize: 12, color: '#6B7280' }}>{g.deliveryType === 'HOME' ? '🏠' : '🏢'} {g.deliveryType}</td>
                      <td style={{ padding: '11px 13px' }}><span style={pill(g.status)}>{g.status.replace(/_/g, ' ')}</span></td>
                      <td style={{ padding: '11px 13px', fontSize: 11, color: '#9CA3AF', whiteSpace: 'nowrap' }}>{fmtIstDate(g.createdAt, { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                      <td style={{ padding: '11px 13px' }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <button onClick={() => setInvoiceGroup(g)} style={{ padding: '5px 9px', borderRadius: 6, border: '1px solid #E5E7EB', cursor: 'pointer', fontSize: 11, fontWeight: 700, background: '#fff', color: '#4B5563', whiteSpace: 'nowrap' }}>🧾 Invoice</button>
                          {g.refundNeedsRetry && (
                            <button disabled={isAdv} onClick={() => retryRefund(g)} title="The Razorpay refund for this order failed — retry it" style={{ padding: '5px 9px', borderRadius: 6, border: '1px solid #FCA5A5', cursor: 'pointer', fontSize: 11, fontWeight: 700, background: '#FEE2E2', color: '#B91C1C', whiteSpace: 'nowrap' }}>
                              {isAdv ? '…' : '⚠ Retry Refund'}
                            </button>
                          )}
                          {next && (
                            <button disabled={isAdv} onClick={() => updateStatus(g, next)} style={{ padding: '5px 11px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700, background: STATUS_COLORS[next]?.bg || '#F3F4F6', color: STATUS_COLORS[next]?.color || '#374151', whiteSpace: 'nowrap' }}>
                              {isAdv ? '…' : `→ ${next.replace(/_/g, ' ')}`}
                            </button>
                          )}
                          {(g.status === 'IN_TRANSIT' || g.status === 'DELIVERED') && (
                            <button disabled={isAdv} onClick={() => updateStatus(g, 'RTO')} style={{ padding: '5px 9px', borderRadius: 6, border: '1px solid #FCA5A5', cursor: 'pointer', fontSize: 11, fontWeight: 700, background: '#FEE2E2', color: '#B91C1C', whiteSpace: 'nowrap' }}>RTO</button>
                          )}
                          {!next && g.status !== 'IN_TRANSIT' && g.status !== 'DELIVERED' && (
                            <span style={{ fontSize: 11, color: '#D1D5DB' }}>—</span>
                          )}
                        </div>
                      </td>
                    </tr>
                    {isExp && (
                      <tr style={{ background: '#FFF8EC', borderBottom: '1px solid #FCCF7A' }}>
                        <td colSpan={9} style={{ padding: '0 13px 14px' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, paddingTop: 12 }}>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', marginBottom: 6, letterSpacing: '0.5px' }}>DELIVERY ADDRESS</div>
                              {g.deliveryType === 'HOME' ? (
                                <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.7 }}>
                                  <div style={{ fontWeight: 600 }}>{g.recipientName}</div>
                                  <div>{g.phone}{g.alternatePhone ? ` / ${g.alternatePhone}` : ''}</div>
                                  <div>{g.addressLine1}{g.addressLine2 ? `, ${g.addressLine2}` : ''}</div>
                                  <div>{g.city}, {g.state} — {g.pincode}</div>
                                </div>
                              ) : (
                                <div style={{ fontSize: 13, color: '#374151' }}><div style={{ fontWeight: 600 }}>{g.officeLocation}</div></div>
                              )}
                            </div>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', marginBottom: 6, letterSpacing: '0.5px' }}>ORDER INFO</div>
                              <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.8 }}>
                                <div><span style={{ color: '#9CA3AF' }}>Company: </span>{tenantName(g.tenantId)}</div>
                                {g.products.map(p => {
                                  const variant = [p.selectedSize, p.selectedColor].filter(Boolean).join(' · ');
                                  return (
                                    <div key={p.orderId}>
                                      <span style={{ color: '#9CA3AF' }}>#{p.orderId}: </span>{p.productName}
                                      {variant && <span style={{ color: '#9CA3AF', fontSize: 12 }}> ({variant})</span>}
                                      {p.sku && <span style={{ color: '#9CA3AF', fontSize: 12 }}> · SKU: {p.sku}</span>}
                                    </div>
                                  );
                                })}
                                {g.campaignName && <div><span style={{ color: '#9CA3AF' }}>Campaign: </span>{g.campaignName}</div>}
                                <div><span style={{ color: '#9CA3AF' }}>Placed: </span>{fmtIstDateTime(g.createdAt)}</div>
                                {g.deliveryDate && (
                                  <div><span style={{ color: '#9CA3AF' }}>Expected delivery: </span>{new Date(g.deliveryDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        </div>
      )}

      {invoiceGroup && (
        <AdminOrderInvoiceModal
          order={invoiceGroup}
          tenantName={tenantName(invoiceGroup.tenantId)}
          inrPerPoint={tenants.find(t => t.id === invoiceGroup.tenantId)?.inrPerPoint}
          onClose={() => setInvoiceGroup(null)}
        />
      )}
    </div>
  );
}

// ── Campaign modal ────────────────────────────────────────────
function CampaignModal({ campaign, tenantId, onClose, onSaved }) {
  const [form, setForm] = useState({
    name:                 campaign?.name                 || '',
    description:          campaign?.description          || '',
    startDate:            campaign?.startDate            || '',
    endDate:              campaign?.endDate              || '',
    maxOrdersPerEmployee: campaign?.maxOrdersPerEmployee != null ? String(campaign.maxOrdersPerEmployee) : '1',
    status:               campaign?.status               || 'ACTIVE',
    defaultCategoryId:    campaign?.defaultCategoryId != null ? String(campaign.defaultCategoryId) : '',
  });
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get('/categories').then(r => setCategories((r.data || []).slice().sort((a, b) => a.name.localeCompare(b.name)))).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    const payload = {
      tenantId,
      name:        form.name.trim(),
      description: form.description || null,
      startDate:   form.startDate   || null,
      endDate:     form.endDate     || null,
      maxOrdersPerEmployee: form.maxOrdersPerEmployee ? parseInt(form.maxOrdersPerEmployee, 10) : null,
      status:      form.status,
      defaultCategoryId: form.defaultCategoryId ? Number(form.defaultCategoryId) : null,
    };
    try {
      if (campaign?.id) {
        await api.put(`/admin/brandstore/campaigns/${campaign.id}`, payload);
      } else {
        await api.post('/admin/brandstore/campaigns', payload);
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
      <form onSubmit={handleSubmit} style={{ background: '#fff', borderRadius: 12, padding: 28, width: 480, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <h3 style={{ fontSize: 17, fontWeight: 700, color: '#111827', margin: '0 0 20px' }}>
          {campaign ? 'Edit Campaign' : 'New Campaign'}
        </h3>

        {error && <div style={{ color: '#B91C1C', background: '#FEE2E2', borderRadius: 7, padding: '8px 12px', marginBottom: 16, fontSize: 13 }}>{error}</div>}

        <div style={{ marginBottom: 14 }}>
          <label style={label}>Campaign Name *</label>
          <input style={input} value={form.name} onChange={e => set('name', e.target.value)} required placeholder="e.g. Welcome Gift 2025" />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={label}>Description</label>
          <textarea style={{ ...input, resize: 'vertical', minHeight: 70 }} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Optional description shown to employees" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
          <div>
            <label style={label}>Start Date</label>
            <input style={input} type="date" value={form.startDate} onChange={e => set('startDate', e.target.value)} />
          </div>
          <div>
            <label style={label}>End Date</label>
            <input style={input} type="date" value={form.endDate} onChange={e => set('endDate', e.target.value)} />
          </div>
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={label}>Max Orders per Employee (leave blank for unlimited)</label>
          <input style={input} type="number" min="1" value={form.maxOrdersPerEmployee} onChange={e => set('maxOrdersPerEmployee', e.target.value)} placeholder="Unlimited" />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={label}>Default Category</label>
          <select style={input} value={form.defaultCategoryId} onChange={e => set('defaultCategoryId', e.target.value)}>
            <option value="">Use the store's default</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <p style={{ fontSize: 11, color: '#6B7280', margin: '5px 0 0' }}>
            While shoppers are in this campaign, the store opens on this category instead of the store's default. If the campaign has no products in it, it opens on "All".
          </p>
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={label}>Status</label>
          <select style={input} value={form.status} onChange={e => set('status', e.target.value)}>
            <option value="ACTIVE">Active</option>
            <option value="PAUSED">Paused (hidden from employees)</option>
            <option value="ENDED">Ended</option>
          </select>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
          <button type="button" style={btnGhost} onClick={onClose}>Cancel</button>
          <button type="submit" style={btnPrimary} disabled={saving}>{saving ? 'Saving…' : 'Save Campaign'}</button>
        </div>
      </form>
    </div>
  );
}


// ── Reward Points tab ────────────────────────────────────────
const TX_LABELS = {
  UPLOAD:            'Balance updated (CSV)',
  TOPUP:              'Points added (top-up)',
  ORDER_DEDUCTION:    'Order placed',
  ORDER_REFUND:       'Order returned',
  MANUAL_ADJUSTMENT:  'Manual correction',
};

function EmployeeHistoryModal({ tenantId, email, onClose }) {
  const [page, setPage]             = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [history, setHistory]       = useState(null);

  useEffect(() => {
    setHistory(null);
    api.get(`/admin/brandstore/tenants/${tenantId}/reward-balances/${encodeURIComponent(email)}/history`, { params: { page, size: 10 } })
      .then(r => { setHistory(r.data.content || []); setTotalPages(r.data.totalPages || 0); })
      .catch(() => setHistory([]));
  }, [tenantId, email, page]);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 480, maxWidth: '95vw', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: '#111827', margin: 0 }}>{email}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#9CA3AF' }}>✕</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {history === null ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF', fontSize: 13 }}>Loading…</div>
          ) : history.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF', fontSize: 13 }}>No activity yet.</div>
          ) : (
            history.map(tx => (
              <div key={tx.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 2px', borderBottom: '1px solid #F3F4F6' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{TX_LABELS[tx.type] || tx.type}</div>
                  <div style={{ fontSize: 11, color: '#9CA3AF' }}>
                    {fmtIstDateTime(tx.createdAt, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    {tx.note ? ` · ${tx.note}` : ''}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: tx.amount > 0 ? '#15803D' : '#B91C1C' }}>
                    {tx.amount > 0 ? '+' : ''}{tx.amount.toLocaleString('en-IN')}
                  </div>
                  <div style={{ fontSize: 10, color: '#9CA3AF' }}>bal. {tx.balanceAfter.toLocaleString('en-IN')}</div>
                </div>
              </div>
            ))
          )}
        </div>

        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14, paddingTop: 12, borderTop: '1px solid #F3F4F6' }}>
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={{ ...btnGhost, padding: '5px 12px', fontSize: 12, opacity: page === 0 ? 0.5 : 1 }}>← Prev</button>
            <span style={{ fontSize: 12, color: '#9CA3AF' }}>Page {page + 1} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} style={{ ...btnGhost, padding: '5px 12px', fontSize: 12, opacity: page >= totalPages - 1 ? 0.5 : 1 }}>Next →</button>
          </div>
        )}
      </div>
    </div>
  );
}

function RewardBalancesTab({ tenants }) {
  const { addToast } = useToast();
  const pointsTenants = tenants.filter(t =>
    (t.paymentMode === 'POINTS' || t.paymentMode === 'POINTS_OR_MONEY') && t.pointsManagedBy !== 'PARTNER'
  );
  const [tenantId, setTenantId] = useState('');
  const [balances, setBalances] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [search, setSearch]     = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage]         = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [editingEmail, setEditingEmail] = useState(null);
  const [editValue, setEditValue]       = useState('');
  const [historyEmail, setHistoryEmail] = useState(null);

  useEffect(() => {
    if (pointsTenants.length === 1 && !tenantId) setTenantId(String(pointsTenants[0].id));
  }, [pointsTenants, tenantId]);

  // Debounce search input so it doesn't fire a request per keystroke; reset to page 1 on a new search.
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(0); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(0); }, [tenantId]);

  const load = useCallback(() => {
    if (!tenantId) { setBalances([]); setTotalPages(0); return; }
    setLoading(true);
    api.get(`/admin/brandstore/tenants/${tenantId}/reward-balances`, { params: { page, size: 20, search: debouncedSearch } })
      .then(r => { setBalances(r.data.content || []); setTotalPages(r.data.totalPages || 0); setLoading(false); })
      .catch(() => setLoading(false));
  }, [tenantId, page, debouncedSearch]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !tenantId) return;
    setUploading(true);
    setUploadResult(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post(`/admin/brandstore/tenants/${tenantId}/reward-balances/upload`, formData);
      setUploadResult(res.data);
      load();
    } catch (err) {
      setUploadResult({ error: err.response?.data?.error || 'Upload failed.' });
    } finally {
      setUploading(false);
    }
  };

  const startEdit = (b) => { setEditingEmail(b.email); setEditValue(String(b.balance)); };

  const saveEdit = async (email) => {
    const balance = parseInt(editValue, 10);
    if (Number.isNaN(balance)) return;
    try {
      await api.put(`/admin/brandstore/tenants/${tenantId}/reward-balances/${encodeURIComponent(email)}`, { balance });
      setEditingEmail(null);
      load();
      addToast(`Balance updated to ${balance} points.`, 'success');
    } catch {
      addToast('Failed to update balance.', 'error');
    }
  };


  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: 0 }}>Reward Points</h2>
          <p style={{ fontSize: 13, color: '#9CA3AF', margin: '4px 0 0' }}>
            eYantra-managed balances for companies without an external rewards partner (e.g. Bonusly). Upload a CSV to seed or refresh balances.
          </p>
        </div>
      </div>

      {pointsTenants.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#9CA3AF' }}>
          No companies are set to "Employees pay with reward points" yet. Set a company's Payment Mode in the Companies tab first.
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap', alignItems: 'center' }}>
            <select value={tenantId} onChange={e => { setTenantId(e.target.value); setUploadResult(null); }} style={{ ...input, width: 'auto', padding: '8px 10px' }}>
              <option value="">Select company…</option>
              {pointsTenants.map(t => <option key={t.id} value={t.id}>{t.displayName}</option>)}
            </select>
            {tenantId && (
              <>
                <input style={{ ...input, width: 260 }} placeholder="Search email or name…" value={search} onChange={e => setSearch(e.target.value)} />
                <label style={{ ...btnPrimary, display: 'inline-flex', alignItems: 'center', cursor: uploading ? 'default' : 'pointer', opacity: uploading ? 0.6 : 1 }}>
                  {uploading ? 'Uploading…' : '⬆ Upload CSV'}
                  <input type="file" accept=".csv" onChange={handleUpload} disabled={uploading} style={{ display: 'none' }} />
                </label>
                <button onClick={load} style={{ marginLeft: 'auto', ...btnGhost }}>↻ Refresh</button>
              </>
            )}
          </div>

          {uploadResult && (
            <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 8, fontSize: 12, background: uploadResult.error ? '#FEE2E2' : '#DCFCE7', color: uploadResult.error ? '#B91C1C' : '#15803D' }}>
              {uploadResult.error
                ? uploadResult.error
                : `${uploadResult.created} created, ${uploadResult.updated} updated, ${uploadResult.skipped} skipped.`}
              {uploadResult.errors?.length > 0 && (
                <div style={{ marginTop: 6 }}>{uploadResult.errors.map((e, i) => <div key={i}>{e}</div>)}</div>
              )}
            </div>
          )}

          <p style={{ fontSize: 11, color: '#6B7280', margin: '0 0 14px' }}>
            CSV columns: <code>email, fullName, officeLocation, points</code>. Each row's points are <strong>added</strong> to that employee's balance — new emails are created at the listed amount, existing ones get topped up. Use the same file format for the initial roster and later grants. Employees not listed are left untouched. To correct someone's balance to an exact value, use Adjust below instead of a CSV.
          </p>

          {!tenantId ? null : loading ? (
            <div style={{ textAlign: 'center', padding: 60, color: '#9CA3AF' }}>Loading…</div>
          ) : balances.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: '#9CA3AF' }}>
              {debouncedSearch ? 'No balances match your search.' : 'No balances yet. Upload a CSV to get started.'}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <div style={{ background: '#fff', borderRadius: 12, border: '1.5px solid #E5E7EB', overflow: 'hidden', minWidth: 600 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#F9FAFB', borderBottom: '1.5px solid #E5E7EB' }}>
                      {['Email', 'Name', 'Office', 'Balance', ''].map(h => (
                        <th key={h} style={{ padding: '11px 13px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {balances.map((b, idx) => (
                      <tr key={b.email} style={{ borderBottom: '1px solid #F3F4F6', background: idx % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                        <td style={{ padding: '10px 13px', fontSize: 12, color: '#374151' }}>{b.email}</td>
                        <td style={{ padding: '10px 13px', fontSize: 12, color: '#374151' }}>{b.fullName || '—'}</td>
                        <td style={{ padding: '10px 13px', fontSize: 12, color: '#6B7280' }}>{b.officeLocation || '—'}</td>
                        <td style={{ padding: '10px 13px', fontSize: 13, fontWeight: 700, color: '#111827' }}>
                          {editingEmail === b.email ? (
                            <input
                              type="number" value={editValue} onChange={e => setEditValue(e.target.value)}
                              style={{ ...input, width: 90, padding: '4px 8px' }} autoFocus
                            />
                          ) : b.balance.toLocaleString('en-IN')}
                        </td>
                        <td style={{ padding: '10px 13px' }}>
                          {editingEmail === b.email ? (
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button onClick={() => saveEdit(b.email)} style={{ ...btnPrimary, padding: '4px 10px', fontSize: 11 }}>Save</button>
                              <button onClick={() => setEditingEmail(null)} style={{ ...btnGhost, padding: '4px 10px', fontSize: 11 }}>Cancel</button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button onClick={() => startEdit(b)} style={{ ...btnGhost, padding: '4px 10px', fontSize: 11 }}>Adjust</button>
                              <button onClick={() => setHistoryEmail(b.email)} style={{ ...btnGhost, padding: '4px 10px', fontSize: 11 }}>History</button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginTop: 14 }}>
                  <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={{ ...btnGhost, padding: '6px 14px', fontSize: 12, opacity: page === 0 ? 0.5 : 1 }}>← Prev</button>
                  <span style={{ fontSize: 12, color: '#9CA3AF' }}>Page {page + 1} of {totalPages}</span>
                  <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} style={{ ...btnGhost, padding: '6px 14px', fontSize: 12, opacity: page >= totalPages - 1 ? 0.5 : 1 }}>Next →</button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {historyEmail && (
        <EmployeeHistoryModal tenantId={tenantId} email={historyEmail} onClose={() => setHistoryEmail(null)} />
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────
export default function BrandstoreManagement() {
  const { user } = useAuth() || {};
  // API keys are ADMIN-only on the server too (SecurityConfig); this just hides the tab.
  const isAdmin = user?.role === 'ADMIN';
  const access = brandstoreAccessFor(user?.role);
  const [activeTab, setActiveTab] = useState('companies');
  const [tenants, setTenants]     = useState([]);

  // Refetched on tab switch so settings changed in the Companies tab (e.g. turning on
  // "Display stock purchase?") show up in the other tabs' company filters without a reload.
  useEffect(() => {
    api.get('/admin/brandstore/tenants')
      .then(r => setTenants(r.data || []))
      .catch(() => {});
  }, [activeTab]);

  const tabBtn = (key, label) => (
    <button
      key={key}
      onClick={() => setActiveTab(key)}
      style={{
        padding: '8px 18px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, transition: 'all 0.15s',
        background: activeTab === key ? 'var(--color-primary,#F29213)' : '#fff',
        color: activeTab === key ? '#fff' : '#4B5563',
        boxShadow: activeTab === key ? 'none' : '0 1px 4px rgba(0,0,0,0.07)',
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ padding: '24px 0' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: '0 0 4px', letterSpacing: '-0.5px' }}>Brandstore Management</h1>
        <p style={{ fontSize: 13, color: '#9CA3AF', margin: 0 }}>Create company portals, assign products, and track orders</p>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        {tabBtn('companies', '🏢 Companies')}
        {tabBtn('orders', '📦 Orders')}
        {access.canViewRewards && tabBtn('rewards', '🎯 Reward Points')}
        {tabBtn('billing', '💰 Billing')}
        {tabBtn('stock', '📦 Inventory')}
        {/* Vouchers are money — ADMIN only, same as reward points (and on the server). */}
        {isAdmin && tabBtn('vouchers', '🎟️ Vouchers')}
        {tabBtn('returns', '↩️ Partner Returns')}
        {isAdmin && tabBtn('apikeys', '🔑 Partner API')}
      </div>

      {!access.canManage && (
        <div role="note" style={{ background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: 8, padding: '10px 14px', marginBottom: 18, fontSize: 13, color: '#075985' }}>
          View-only: you can update order statuses{access.canProcessReturns ? ' and process partner returns' : ''}. Everything else is managed by an admin.
        </div>
      )}

      <BrandstoreAccessContext.Provider value={access}>
        {activeTab === 'companies' && <CompaniesTab />}
        {activeTab === 'orders'    && <OrdersTab tenants={tenants} />}
        {activeTab === 'rewards'   && access.canViewRewards && <RewardBalancesTab tenants={tenants} />}
        {activeTab === 'billing'   && <BillingTab tenants={tenants} />}
        {activeTab === 'stock'     && <BrandstoreStockTab tenants={tenants} />}
        {activeTab === 'vouchers' && isAdmin && <BrandstoreVouchersTab tenants={tenants} />}
        {activeTab === 'returns'   && <PartnerReturnsTab tenants={tenants} />}
        {activeTab === 'apikeys' && isAdmin && <PartnerApiKeysTab tenants={tenants} />}
      </BrandstoreAccessContext.Provider>
    </div>
  );
}
